import { Heading, Stack, useToast, VStack, Spinner } from "@chakra-ui/react";
import { useKydEvent } from "../../hooks/useKydEvent";
import { useParams } from "react-router-dom";
import actions from "../../../../actions";
import { useCallback, useEffect, useState } from "react";
import EventSummarySection from "../../SummarySection/EventSummarySection";
import { createOnUpdateEvent, EditModalType } from "../../../../utils";
import { useDialog } from "../../../../Common/Dialog";
import { PayoutsTable } from "./PayoutsTable";
import {
  EventDetailPayoutsSection,
  EventDetailSummarySection,
  EventDetailSummarySectionData,
  KYDAdminEventDetailSectionsTypeEnum,
  EventDetailAttendeesSection,
} from "common/interfaces/KYDEvent";

const summarySectionType = KYDAdminEventDetailSectionsTypeEnum.SUMMARY;
const payoutsSectionType = KYDAdminEventDetailSectionsTypeEnum.PAYOUTS;

export interface PayoutsNavSectionData {
  summary?: EventDetailSummarySection;
  payouts?: EventDetailPayoutsSection;
  attendees?: EventDetailAttendeesSection;
}

const PayoutsNavSection = () => {
  const { event_id } = useParams();
  const { kydEvent, refetch } = useKydEvent(event_id, {
    sectionTypes: ["summary", "payouts"],
  });
  const toast = useToast();
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState<PayoutsNavSectionData>();

  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const dialog = useDialog();

  const summarySection = data?.summary;
  const payoutsSection = data?.payouts;

  const fetchData = useCallback(async () => {
    if (!kydEvent?.id) return;

    setLoading(true);

    try {
      const response = await actions.ADMIN.fetchEventSections(kydEvent?.id, [
        summarySectionType,
        payoutsSectionType,
      ]);
      setData({
        summary: response.summary as EventDetailSummarySection,
        payouts: response.payouts as EventDetailPayoutsSection,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [actions, kydEvent, setLoading]);

  const onUpdateEvent = useCallback(
    createOnUpdateEvent({
      dialog,
      event_id,
      refetch,
      setLoading: (isLoading) => setLoading(isLoading),
      setModalType,
      toast,
    }),
    [event_id, refetch, setLoading, dialog, toast, setModalType]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Stack maxW="5xl" w="full">
      {loading ? (
        <VStack w="100%">
          <Spinner />
        </VStack>
      ) : (
        <VStack w="100%">
          {summarySection ? (
            <EventSummarySection
              isLoading={loading}
              data={summarySection?.data as EventDetailSummarySectionData}
              permissions={(summarySection?.permissions || []).filter(
                (permission) => permission !== "write"
              )}
              onUpdateEvent={onUpdateEvent}
              modalType={modalType}
              setModalType={setModalType}
              refetchEvent={refetch}
            />
          ) : null}

          <Stack
            direction={{ base: "column" }}
            justify={"space-between"}
            w="100%"
          >
            <Heading w="full" size="md">
              Payouts
            </Heading>
            {payoutsSection ? (
              <PayoutsTable data={payoutsSection.data} />
            ) : null}
          </Stack>
        </VStack>
      )}
    </Stack>
  );
};

export default PayoutsNavSection;
